import { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash-es";
import { useRatingActions } from "../../hooks/useCallRating";

export const useRatingSubmission = (conversationId: string) => {
  const { submitRating } = useRatingActions();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const submitRatingWithPayload = useCallback(
    async (rating: number, issues: string[] = [], feedback: string = "") => {
      setIsSubmitting(true);
      try {
        await submitRating({
          conversation_id: conversationId,
          rating,
          issues,
          feedback,
        });
        setHasSubmitted(true);
      } catch (error) {
        console.error("Failed to submit rating:", error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [conversationId, submitRating]
  );

  return { submitRatingWithPayload, isSubmitting, hasSubmitted };
};

export const useDebouncedRatingSubmission = (
  _conversationId: string,
  submitRatingFn: (rating: number, issues: string[], feedback: string) => Promise<void>
) => {
  const debouncedSubmitRef = useRef(
    debounce(async (rating: number, issues: string[], feedback: string) => {
      await submitRatingFn(rating, issues, feedback);
    }, 500)
  );

  useEffect(() => {
    return () => {
      debouncedSubmitRef.current.cancel();
    };
  }, []);

  return debouncedSubmitRef.current;
};

export const useIssuesUpdate = (conversationId: string) => {
  const { updateRating } = useRatingActions();
  const debouncedUpdateRef = useRef(
    debounce(async (issues: string[]) => {
      try {
        await updateRating(conversationId, { issues });
      } catch (error) {
        console.error("Failed to update issues:", error);
      }
    }, 500)
  );

  useEffect(() => {
    return () => {
      debouncedUpdateRef.current.cancel();
    };
  }, [conversationId, updateRating]);

  return debouncedUpdateRef.current;
};

